/**
 * Bandeau de restant — **priorité visuelle n°1** de l'écran (specs §6.2).
 *
 * « Le restant est l'information la plus consultée : il doit être lisible sans
 * interaction. » D'où quatre partis pris, inchangés depuis l'étape 5 :
 *   - le restant est le chiffre le plus gros, consommé et objectif sont en
 *     second plan ;
 *   - un dépassement se voit — le **mot** (« de trop ») le dit, jamais un signe
 *     moins ; il n'est ni masqué ni borné ;
 *   - **une teinte par macro**, la même dans toute l'app, pour retrouver la
 *     bonne ligne sans lire — le libellé reste présent et porte l'information ;
 *   - aucune interaction n'est nécessaire pour lire les quatre macros.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * REFONTE « NOURRI » — la carte-héros du prototype
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `design-tokens.md`, principe 1 : **l'anneau reste le seul « gros » objet
 * visuel de l'écran**. C'est ce qui change ici par rapport à la version d'août :
 * les trois petits anneaux de macros deviennent trois cellules à mini-barre.
 *
 * 🔴 **Pas de barre de composition, alors que `design-tokens.md` la range parmi
 * les composants clés.** Elle a été construite, essayée à l'usage et **retirée
 * le 26/08/2026** : « ça ne me sert pas ». Elle montrait la part de chaque macro
 * dans les calories consommées — une information que les trois cellules
 * juste en dessous donnent déjà, en chiffres et en valeur absolue, c'est-à-dire
 * sous la forme dont on se sert pour décider quoi manger ensuite.
 *
 * ⚠️ `CompositionBar` **reste dans la bibliothèque** et dans la galerie : c'est
 * un composant du système, et il est décrit par le document de design. C'est cet
 * écran-ci qui n'en veut pas, pas le système qui la renie.
 *
 * ⚠️ La **hiérarchie de lecture** ne bouge pas d'un cran : on regarde d'abord
 * « combien il me reste à manger aujourd'hui », et seulement ensuite comment le
 * répartir. C'est la conclusion des retours de l'étape 10, et elle survit à la
 * refonte — c'est même ce que le principe 1 formalise.
 *
 * 🔴 **Aucune carte sous ce bandeau** — demandé après essai sur l'appareil : le
 * récapitulatif se pose **directement sur le fond de page**, sans surface
 * blanche ni ombre, contrairement à la `.summary-card` du prototype. Il y gagne
 * ce que le principe 1 cherchait : l'anneau reste le seul gros objet de
 * l'écran, et les cartes blanches ne désignent plus que les repas — c'est-à-dire
 * ce sur quoi on agit.
 *
 * 🔴 **Aucune teinte d'état, nulle part** : ni rouge au dépassement, ni vert à
 * l'objectif atteint. Voir `toneFor` juste en dessous. Les calories portent
 * `--brand`, qui est leur teinte de rôle et non un signal.
 *
 * ⚠️ Aucun `backdrop-filter` ici, et il n'y en a plus nulle part depuis la
 * refonte du 25/08 : ce bandeau fait partie du contenu qui défile.
 */

import type { KcalGap, MacroSet } from '../../../../shared/types';
import type { BarTone } from '../../components/nourri';
import { ProgressBar, ProgressRing } from '../../components/nourri';
import { formatMacro, MACRO_ROWS, type MacroKey } from '../../lib/macros';
import { readMacro, remainingWord } from '../../lib/macroStatus';

/**
 * Teinte d'une jauge — **celle de son rôle, et rien d'autre**.
 *
 * 🔴 Décidé après essai sur l'appareil : une jauge ne change **jamais** de
 * couleur selon son état. Ni rouge au dépassement, ni vert à l'objectif atteint
 * — les deux existaient, et les deux sont partis ensemble parce qu'ils
 * répondaient à la même mauvaise idée : faire porter à la couleur un jugement
 * que l'application n'a pas à porter (specs §6.2, « ne corrige jamais, ne
 * bloque jamais, ne suggère jamais »). Un journal bien rempli finissait
 * constellé de rouge pour une situation ordinaire.
 *
 * Ce qui dit l'état, désormais : le **mot** (« restant » / « de trop »), les
 * **chiffres** (consommé sur objectif), et le remplissage de la jauge.
 *
 * ⚠️ Les calories n'ont pas de teinte propre : elles portent `--brand`
 * (`tokens-nourri.css`).
 *
 * ⚠️ La fonction reste, plutôt que d'écrire la teinte sur place : c'est ici, et
 * nulle part ailleurs, qu'on rouvrirait la question si l'usage la reposait.
 */
function toneFor(macro: MacroKey): BarTone {
  return macro === 'kcal' ? 'brand' : macro;
}

/* ==========================================================================
 * Les trois macros — prototype `.macro-row`
 * ======================================================================= */

function MacroCell({
  macro,
  label,
  unit,
  consumed,
  target,
  remaining,
}: {
  macro: Exclude<MacroKey, 'kcal'>;
  label: string;
  unit: string;
  consumed: number;
  target: number;
  remaining: number;
}) {
  const reading = readMacro(remaining, target, unit);
  const tone = toneFor(macro);

  return (
    <div>
      {/*
        ⚠️ **La pastille double le libellé, elle ne le remplace pas.** C'est la
        condition pour qu'une couleur ait le droit de porter du sens : en
        protanopie deux teintes de macro peuvent se rapprocher, et c'est le mot
        qui départage.
      */}
      <div className="n-macro-cell__top">
        <span className={`n-dot n-dot--${macro}`} aria-hidden="true" />
        {label}
      </div>

      {/*
        Prototype `.macro-cell .val` : le consommé en gros, l'objectif en petit
        juste derrière. C'est bien le **consommé** qui est affiché ici, pas le
        restant — le restant a son anneau au-dessus, et la cellule répond à
        l'autre question : « où j'en suis sur cette macro ».
      */}
      <div className="n-macro-cell__val tnum">
        {formatMacro(consumed, unit)}
        <span className="n-macro-cell__of">
          /{formatMacro(target, unit)}
          {unit}
        </span>
      </div>

      <ProgressBar
        value={consumed}
        max={target}
        tone={tone}
        size="sm"
        label={`${label} : ${formatMacro(Math.abs(reading.displayed), unit)} ${unit} ${remainingWord(reading)}`}
      />
    </div>
  );
}

/* ==========================================================================
 * Écart kcal
 * ======================================================================= */

/**
 * Écart kcal, affiché **discrètement** (§5.3 : « le front l'affiche
 * discrètement »). Purement informatif : rien ne propose de le corriger, et il
 * n'apparaît que s'il y a effectivement un écart à signaler.
 */
function KcalGapNote({ gap }: { gap: KcalGap }) {
  if (Math.round(gap.delta) === 0) return null;
  const sign = gap.delta > 0 ? '+' : '−';
  return (
    <p className="n-hint" style={{ marginTop: 14 }}>
      Objectif kcal saisi : {sign}
      {Math.abs(Math.round(gap.delta)).toLocaleString('fr-FR')} kcal par rapport au total 4/4/9 de
      P/G/L ({Math.round(gap.theoretical).toLocaleString('fr-FR')} kcal). Indicatif.
    </p>
  );
}

/* ==========================================================================
 * Bandeau
 * ======================================================================= */

/**
 * ⚠️ Les trois macros sont dérivées de `MACRO_ROWS` par **exclusion** des kcal,
 * jamais réécrites en dur : l'ordre des macros est fixé une seule fois dans
 * `lib/macros.ts` et doit rester le même sur tous les écrans (§6.2).
 */
const AUTRES = MACRO_ROWS.filter((row) => row.key !== 'kcal');

export function MacroBanner({
  targets,
  consumed,
  remaining,
  kcalGap,
  confirming = false,
  still = false,
}: {
  targets: MacroSet;
  consumed: MacroSet;
  remaining: MacroSet;
  kcalGap: KcalGap;
  /**
   * 🔴 **Le retour de validation d'une saisie se joue ici**, et pas sur le
   * bouton qui l'a déclenchée : celui-ci vit dans une feuille modale qui se
   * referme au même instant, donc une animation dessus ne serait jamais vue.
   * Le bandeau, lui, est exactement ce qu'on regarde après avoir ajouté —
   * c'est le restant qu'on venait chercher (specs §6.2).
   *
   * C'est le repli visuel de l'haptique, que Safari iOS ne fournit pas
   * (cf. `lib/haptics.ts`). `prefers-reduced-motion` le neutralise.
   */
  confirming?: boolean;
  /**
   * 🔴 **Le remplissage ne s'anime pas quand la journée arrive par un
   * GLISSEMENT** — demandé après usage *(31/08/2026)*.
   *
   * L'anneau et les barres remplissent par une transition CSS. `goTo()` sert
   * aux deux chemins — les flèches et le pager — et navigue vers la même
   * route : ce composant n'est donc jamais démonté, ses valeurs changent sur un
   * élément qui persiste, et la transition rejoue. Aux flèches c'est ce qu'on
   * veut ; au glissement elle se superpose à un mouvement déjà en cours, et
   * l'écran fait deux choses pour un seul geste.
   *
   * ⚠️ **On coupe la transition, on ne la raccourcit pas** : une transition
   * plus courte resterait une seconde animation. Ce qui est demandé, c'est
   * qu'il n'y en ait qu'une.
   */
  still?: boolean;
}) {
  const kcalReading = readMacro(remaining.kcal, targets.kcal, 'kcal');
  const kcalMot = remainingWord(kcalReading);

  return (
    /*
      ⚠️ Un `<section>` nu, plus une `Card` : le récapitulatif se pose sur le
      fond de page. `<section>` porte son rôle tout seul, il n'y a donc pas de
      `role="group"` à ajouter comme il l'aurait fallu sur le `<div>` d'une
      carte.
    */
    <section
      /*
        ⚠️ **La classe est posée sur le CONTENEUR, pas sur chaque jauge.** Elle
        coupe les transitions de l'anneau *et* des quatre barres en une règle,
        et surtout elle ne demande rien à `ProgressRing` ni à `ProgressBar` —
        deux composants de la bibliothèque, employés ailleurs, qui n'ont pas à
        connaître le pager du journal.
      */
      className={[
        'n-summary',
        confirming ? 'is-confirming' : '',
        still ? 'is-still' : '',
      ]
        .filter((c) => c !== '')
        .join(' ')}
      aria-label="Restant de macros"
    >
      {/* --- Calories : l'anneau, seul « gros » objet de l'écran ---------- */}
      <div className="n-ring-wrap">
        <ProgressRing
          value={consumed.kcal}
          max={targets.kcal}
          tone={toneFor('kcal')}
          size={186}
          /*
            🔴 **Valeur absolue, et aucun signe moins** : le mot porte déjà le
            sens. « −5 kcal de trop » se lit une fois de travers — le moins
            semble annoncer un manque alors que la phrase annonce un excès.
          */
          primary={formatMacro(Math.abs(kcalReading.displayed), 'kcal')}
          caption={`kcal ${kcalMot}`}
          secondary={`${formatMacro(consumed.kcal, 'kcal')} / ${formatMacro(targets.kcal, 'kcal')}`}
          label={`Calories : ${formatMacro(Math.abs(kcalReading.displayed), 'kcal')} kcal ${kcalMot}`}
        />
      </div>

      {/* --- Les trois macros, sur une seule ligne ------------------------ */}
      <div className="n-macro-grid">
        {AUTRES.map((row) => (
          <MacroCell
            key={row.key}
            /*
              ⚠️ Le rétrécissement est sûr **par construction** : `AUTRES` est
              `MACRO_ROWS` filtré des kcal, juste au-dessus. Il est nécessaire
              parce que la classe de pastille est composée depuis la clé
              (`n-dot--protein`) et qu'il n'existe pas de `n-dot--kcal` — les
              calories portent `--brand`, pas une teinte à elles.
            */
            macro={row.key as Exclude<MacroKey, 'kcal'>}
            label={row.label}
            unit={row.unit}
            consumed={consumed[row.key]}
            target={targets[row.key]}
            remaining={remaining[row.key]}
          />
        ))}
      </div>

      <KcalGapNote gap={kcalGap} />
    </section>
  );
}
